import React from "react";

const formatMatchDate = (match) => {
    const rawValue = match?.startTime || match?.date;
    if (!rawValue) {
        return "-";
    }

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return String(rawValue);
    }

    return parsedDate.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getWinnerId = (match) => {
    if (match?.winnerTeam?.id != null) {
        return match.winnerTeam.id;
    }
    if (!match?.sets?.length) return null;

    let participant1Wins = 0;
    let participant2Wins = 0;

    match.sets.forEach((set) => {
        const left = Number(set.player1Score);
        const right = Number(set.player2Score);

        if (Number.isNaN(left) || Number.isNaN(right)) {
            return;
        }

        if (left > right) participant1Wins += 1;
        else if (right > left) participant2Wins += 1;
    });

    if (participant1Wins > participant2Wins) return match.team1?.id ?? match.team1Id ?? null;
    if (participant2Wins > participant1Wins) return match.team2?.id ?? match.team2Id ?? null;
    return null;
};

const getSetWinnerId = (set, match) => {
    if (!set || !match) return null;

    const left = Number(set.player1Score);
    const right = Number(set.player2Score);

    if (Number.isNaN(left) || Number.isNaN(right)) return null;
    if (left > right) return match.team1?.id ?? match.team1Id ?? null;
    if (right > left) return match.team2?.id ?? match.team2Id ?? null;
    return null;
};

const getSetValue = (match, setNumber, participant1Id) => {
    const set = match?.sets?.find((entry) => entry.setNumber === setNumber);
    if (!set) return "-";

    if ((match.team1?.id ?? match.team1Id) === participant1Id) {
        return `${set.player1Score}:${set.player2Score}`;
    }

    return `${set.player2Score}:${set.player1Score}`;
};

const getSetClass = (match, setNumber) => {
    const set = match?.sets?.find((entry) => entry.setNumber === setNumber);
    const setWinner = getSetWinnerId(set, match);
    const matchWinner = getWinnerId(match);
    if (!setWinner || !matchWinner) return undefined;
    return setWinner === matchWinner ? "groupphase-winner-cell" : undefined;
};

const getParticipantName = (participant) => {
    if (!participant) return "-";
    if (participant.displayName) return participant.displayName;
    if (participant.name) return participant.name;
    if (Array.isArray(participant.players) && participant.players.length > 0) {
        return participant.players.map((player) => player.name).join(" / ");
    }
    return `Team ${participant.id}`;
};

const getManageButtonProps = (manageAllowed, title) => ({
    disabled: !manageAllowed,
    title: manageAllowed ? title : "Nur Admins oder beteiligte Spieler können diese Aktion ausführen.",
    style: !manageAllowed ? {opacity: 0.5, cursor: "not-allowed"} : undefined,
});

const GroupPhaseMatchTableRow = ({
                                     participant1,
                                     participant2,
                                     match,
                                     showSetColumns = true,
                                     deleteAllowed,
                                     resultAllowed,
                                     isAdminUser = false,
                                     useParticipantPerspective = false,
                                     onEditMatch,
                                     onDeleteMatch,
                                     onScheduleMatch,
                                     onDirectResultEntry,
                                 }) => {
    const effectiveResultAllowed = resultAllowed !== undefined ? resultAllowed : deleteAllowed;
    const winnerId = getWinnerId(match);
    const hasResult = Boolean(match?.sets?.length);
    const showAdminActions = useParticipantPerspective || isAdminUser;

    return (
        <tr key={`${participant1.id}-${participant2.id}`}>
            <td className={winnerId === participant1.id ? "groupphase-winner-cell" : undefined}>
                {getParticipantName(participant1)}
            </td>
            <td className={winnerId === participant2.id ? "groupphase-winner-cell" : undefined}>
                {getParticipantName(participant2)}
            </td>
            {!hasResult && <td>{formatMatchDate(match)}</td>}
            {!hasResult && <td>{match?.court || "-"}</td>}

            {hasResult ? (
                <>
                    {[1, 2, 3].map((setNumber) => (
                        <td
                            key={setNumber}
                            className={getSetClass(match, setNumber)}
                        >
                            {getSetValue(match, setNumber, participant1.id)}
                        </td>
                    ))}
                    <td>
                        <div className="groupphase-action-buttons">
                        <button
                            className="groupphase-button groupphase-action-btn"
                            onClick={() => onEditMatch(match)}
                            {...getManageButtonProps(effectiveResultAllowed, "Ergebnis dieser Begegnung ändern")}
                        >
                            ✏️ ändern
                        </button>
                        {typeof onDeleteMatch === "function" && (
                            <button
                                className="groupphase-button delete-button groupphase-delete-btn"
                                onClick={() => onDeleteMatch(match)}
                                {...getManageButtonProps(deleteAllowed, "Ergebnis dieser Begegnung löschen")}
                            >
                                🗑️
                            </button>
                        )}
                        </div>
                    </td>
                </>
            ) : match ? (
                <>
                    {showSetColumns && <td colSpan={3}></td>}
                    <td>
                        <div className="groupphase-action-buttons">
                        <button
                            className="groupphase-button groupphase-action-btn"
                            onClick={() => onEditMatch(match)}
                            {...getManageButtonProps(effectiveResultAllowed, "Ergebnis für diese Begegnung eintragen")}
                        >
                            ✍️ eintragen
                        </button>
                        {typeof onDeleteMatch === "function" && (
                            <button
                                className="groupphase-button delete-button groupphase-delete-btn"
                                onClick={() => onDeleteMatch(match)}
                                {...getManageButtonProps(deleteAllowed, "Geplante Begegnung löschen")}
                            >
                                🗑️
                            </button>
                        )}
                        </div>
                    </td>
                </>
            ) : (
                <>
                    {showSetColumns && <td>-</td>}
                    {showSetColumns && <td>-</td>}
                    {showSetColumns && <td>-</td>}
                    <td>
                        <div className="groupphase-action-buttons">
                        {showAdminActions && (
                            <button
                                className="groupphase-button groupphase-action-btn"
                                onClick={() => onScheduleMatch(participant1, participant2)}
                                {...getManageButtonProps(deleteAllowed, "Diese Begegnung planen")}
                            >
                                📅 buchen
                            </button>)
                        }
                        {showAdminActions && typeof onDirectResultEntry === "function" && (
                            <button
                                className="groupphase-button groupphase-action-btn"
                                onClick={() => onDirectResultEntry(participant1, participant2)}
                                {...getManageButtonProps(effectiveResultAllowed, "Ergebnis eintragen")}
                            >
                                ✍️ eintragen
                            </button>
                        )}
                        </div>
                    </td>
                </>
            )}
        </tr>
    );
};

export default GroupPhaseMatchTableRow;

